<script setup lang="ts">
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { UiIcon, UiText } from '@shopexperts/shared';
import type { ExpertFilterPreset } from '@shopexperts/legacy-api';

const props = defineProps<{
  presets: ExpertFilterPreset[];
  /** 'all' for the unsaved view, otherwise the preset id as a string. */
  modelValue: string;
  /** True when the live filters no longer match the active preset's snapshot. */
  dirty: boolean;
  loading?: boolean;
  canSave?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  create: [];
  update: [preset: ExpertFilterPreset];
  rename: [preset: ExpertFilterPreset];
  remove: [preset: ExpertFilterPreset];
  discard: [];
}>();

const menuOpenFor = ref<number | null>(null);
const menuRef = ref<HTMLElement | null>(null);

// A plain ref inside v-for would be collected into an array; a function ref
// keeps the single open menu addressable for onClickOutside.
function setMenuRef(el: unknown) {
  menuRef.value = (el as HTMLElement | null) ?? null;
}

onClickOutside(menuRef, () => { menuOpenFor.value = null; });

function select(value: string) {
  menuOpenFor.value = null;
  if (value !== props.modelValue) emit('update:modelValue', value);
}

function toggleMenu(preset: ExpertFilterPreset) {
  menuOpenFor.value = menuOpenFor.value === preset.id ? null : preset.id;
}

function run(action: 'update' | 'rename' | 'remove' | 'discard', preset: ExpertFilterPreset) {
  menuOpenFor.value = null;
  if (action === 'update') emit('update', preset);
  else if (action === 'rename') emit('rename', preset);
  else if (action === 'remove') emit('remove', preset);
  else emit('discard');
}

function isActive(value: string) {
  return props.modelValue === value;
}
</script>

<template>
  <div class="flex items-end gap-4 border-b border-se-gray-300">
    <!-- Wraps rather than scrolls: an overflow container would clip the tab menus. -->
    <div class="min-w-0 flex-1">
      <div class="-mb-px flex flex-wrap items-end gap-x-6">
        <button
          type="button"
          class="shrink-0 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors cursor-pointer"
          :class="isActive('all')
            ? 'border-primary text-primary'
            : 'border-transparent text-se-gray-500 hover:border-se-gray-300 hover:text-se-gray-700'"
          @click="select('all')"
        >
          All experts
        </button>

        <div
          v-for="preset in presets"
          :key="preset.id"
          class="relative shrink-0"
        >
          <div
            class="flex items-center gap-1 border-b-2 transition-colors"
            :class="isActive(String(preset.id))
              ? 'border-primary text-primary'
              : 'border-transparent text-se-gray-500 hover:border-se-gray-300 hover:text-se-gray-700'"
          >
            <button
              type="button"
              class="max-w-[220px] truncate whitespace-nowrap px-1 py-3 text-sm font-medium cursor-pointer"
              :title="preset.name"
              @click="select(String(preset.id))"
            >
              {{ preset.name }}
            </button>

            <span
              v-if="isActive(String(preset.id)) && dirty"
              class="mb-3 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
              title="Unsaved changes to this tab's filters"
            />

            <button
              v-if="isActive(String(preset.id))"
              type="button"
              class="mb-2 rounded p-1 text-current opacity-60 transition-opacity hover:opacity-100 cursor-pointer"
              aria-label="Tab options"
              @click.stop="toggleMenu(preset)"
            >
              <UiIcon name="chevron-down" class="h-3.5 w-3.5" />
            </button>
          </div>

          <Transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="menuOpenFor === preset.id"
              :ref="setMenuRef"
              class="absolute left-0 top-full z-50 mt-1 w-60 origin-top-left rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            >
              <button
                class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                :disabled="!dirty"
                @click="run('update', preset)"
              >
                <UiIcon name="check" class="h-4 w-4 shrink-0 opacity-60" />
                <UiText as="span" variant="p-small">Save current filters here</UiText>
              </button>
              <button
                class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                :disabled="!dirty"
                @click="run('discard', preset)"
              >
                <UiIcon name="close" class="h-4 w-4 shrink-0 opacity-60" />
                <UiText as="span" variant="p-small">Discard changes</UiText>
              </button>
              <div class="my-1 border-t border-gray-100" />
              <button
                class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-50"
                @click="run('rename', preset)"
              >
                <UiIcon name="pencil" class="h-4 w-4 shrink-0 opacity-60" />
                <UiText as="span" variant="p-small">Rename tab</UiText>
              </button>
              <button
                class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-red-600 transition-colors hover:bg-red-50"
                @click="run('remove', preset)"
              >
                <UiIcon name="delete" class="h-4 w-4 shrink-0 opacity-60" />
                <UiText as="span" variant="p-small">Delete tab</UiText>
              </button>
            </div>
          </Transition>
        </div>

        <UiText
          v-if="loading && presets.length === 0"
          as="span"
          variant="caption-normal"
          class="shrink-0 py-3 text-gray-400"
        >
          Loading tabs...
        </UiText>
      </div>
    </div>

    <button
      type="button"
      class="mb-2 flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
      :disabled="!canSave"
      :title="canSave ? 'Save the current filters as a new tab' : 'Apply a filter first to save it as a tab'"
      @click="emit('create')"
    >
      <UiIcon name="plus" class="h-3.5 w-3.5" />
      Save as tab
    </button>
  </div>
</template>
